import { useEffect, useRef, useState } from "react";
import { Bot, Sparkles, ThumbsDown, ThumbsUp, Trash2, X } from "lucide-react";
import type { AskResponse } from "@hub/shared";
import { Avatar, Button, IconButton, Input, Pill, Switch, toast } from "@/ui";
import { ContactChip, api, feedbackPlatform, useEngineQuery, useOpenContact, useSession } from "./_shims";
import { askHub, loadGroupNames, loadStarters } from "./lib/askRules";
import { personStarters } from "./lib/personAnswer";
import { readJson, readString, writeJson } from "./lib/storage";
import s from "./network.module.css";

const HISTORY_KEY = "hub_ask_history";
const FEEDBACK_KEY = "hub_ask_feedback";
const USE_AI_KEY = "hub_ask_use_ai";
const AI_KEY = "hub_ai_key";
const AI_MODEL = "claude-haiku-5-5";
/** How much of an answer goes with a rating to the owner's Feedback tab. */
const EXCERPT_CHARS = 400;

/**
 * The saved conversation, read defensively: turns saved by an older version may lack fields added since (facts,
 * chips, warnings), and a missing list must never take the panel down.
 */
export function loadHistory(): AskTurn[] {
  const raw = readJson<unknown>(HISTORY_KEY, []);
  if (!Array.isArray(raw)) return [];
  const list = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  return raw
    .filter((t): t is AskTurn => !!t && typeof t === "object" && Array.isArray((t as AskTurn).people) && typeof (t as AskTurn).answer === "string")
    .map((t) => ({ ...t, query: typeof t.query === "string" ? t.query : "", chips: list(t.chips), warnings: list(t.warnings), facts: list(t.facts), total: typeof t.total === "number" ? t.total : t.people.length, broadened: !!t.broadened }));
}

export interface AskTurn extends AskResponse {
  id: string;
  /** "rules": composed from the rows. "ai": the model rewrote the composed answer; the rows did not change. */
  source: "rules" | "ai";
  at: string;
}

function factsContext(r: AskResponse): string {
  const people = r.people.map((p) => `${p.displayName}${p.why ? ` (${p.why})` : ""}`).join("\n");
  return [`Answer from the data: ${r.answer}`, people ? `People found (${r.total}):\n${people}` : "People found: none", r.facts.length ? `Details:\n${r.facts.join("\n")}` : null].filter(Boolean).join("\n\n");
}

/**
 * Optional model rewrite of the retrieved facts. Raw fetch because no SDK is installed in this repo; the key never
 * leaves the browser except to Anthropic. Only the answer, the matched people and the detail lines are sent, and the
 * model is told to add nothing: it is a rewrite, never a source.
 */
async function aiRewrite(question: string, r: AskResponse, apiKey: string): Promise<string> {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: { "content-type": "application/json", "x-api-key": apiKey, "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true" },
    body: JSON.stringify({
      model: AI_MODEL,
      max_tokens: 400,
      system:
        "You rewrite an answer about the user's own address book so it reads naturally. The answer and the facts below were retrieved from their data; use only them. Never add a person, a date, a company or a fact that is not listed. Keep every name that is listed. Two or three plain sentences, no markdown, no emoji.",
      messages: [{ role: "user", content: `Question: ${question}\nSearch used: ${r.query}\n\n${factsContext(r)}` }],
    }),
  });
  if (!res.ok) throw new Error(`The model request failed (${res.status}).`);
  const json = (await res.json()) as { content?: { type: string; text?: string }[] };
  const text = (json.content ?? []).filter((b) => b.type === "text").map((b) => b.text ?? "").join("").trim();
  if (!text) throw new Error("The model returned no answer.");
  return text;
}

export function AskPanel({ contactId, onClose }: { contactId?: string; onClose?: () => void }) {
  const open = useOpenContact();
  const contextRow = useEngineQuery("getContactRows", { contactIds: contactId ? [contactId] : [] }, { enabled: Boolean(contactId) });
  const [prefilledFor, setPrefilledFor] = useState<string | null>(null);
  const [history, setHistory] = useState<AskTurn[]>(() => loadHistory());
  const [feedback, setFeedback] = useState<Record<string, "up" | "down">>(() => readJson(FEEDBACK_KEY, {}));
  const { isSignedIn } = useSession();
  // After a thumbs-down: the turn whose "what was wrong" box is open, its text, and the turns already explained.
  const [commenting, setCommenting] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [explained, setExplained] = useState<Set<string>>(() => new Set());
  const [useAi, setUseAi] = useState<boolean>(() => readJson(USE_AI_KEY, false));
  const [starters, setStarters] = useState<string[]>([]);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const hasKey = Boolean(readString(AI_KEY));
  const scroll = useRef<HTMLDivElement>(null);

  useEffect(() => writeJson(HISTORY_KEY, history.slice(-50)), [history]);
  useEffect(() => writeJson(FEEDBACK_KEY, feedback), [feedback]);
  useEffect(() => writeJson(USE_AI_KEY, useAi), [useAi]);
  useEffect(() => {
    scroll.current?.scrollTo({ top: scroll.current.scrollHeight });
  }, [history.length, busy]);
  // The starters come from the workspace itself: its top companies and cities, its newest needs and offers.
  useEffect(() => {
    let live = true;
    void loadStarters().then((list) => {
      if (live) setStarters(list);
    });
    return () => {
      live = false;
    };
  }, []);
  // Opened about a person: answer "tell me about them" at once so the panel reads as being about that person.
  useEffect(() => {
    const row = contextRow.data?.[0];
    if (!contactId || !row || prefilledFor === contactId) return;
    setPrefilledFor(contactId);
    void submit(`Tell me about ${row.displayName}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, contextRow.data, prefilledFor]);

  const submit = async (q: string) => {
    const text = q.trim();
    if (!text || busy) return;
    setBusy(true);
    setQuestion("");
    try {
      const row = contextRow.data?.[0];
      const groupNames = await loadGroupNames();
      const response = await askHub(text, { person: row ? { contactId: row.contactId, displayName: row.displayName, firstName: row.firstName } : null, groupNames });
      let answer = response.answer;
      let source: AskTurn["source"] = "rules";
      const key = useAi && hasKey ? readString(AI_KEY) : null;
      if (key && response.people.length > 0) {
        try {
          answer = await aiRewrite(text, response, key);
          source = "ai";
        } catch (e) {
          toast(e instanceof Error ? `${e.message} Showing the answer from your data.` : "Model unavailable. Showing the answer from your data.", "neutral");
        }
      }
      const turn: AskTurn = { ...response, chips: response.chips ?? [], warnings: response.warnings ?? [], facts: response.facts ?? [], answer, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, source, at: new Date().toISOString() };
      setHistory((h) => [...h, turn]);
    } catch (e) {
      toast(e instanceof Error ? e.message : "Could not answer that.", "danger");
    } finally {
      setBusy(false);
    }
  };

  // Each rating also goes to the server for the owner's review (best effort: the thumb stays set either way).
  const sendRating = (t: AskTurn, rating: "up" | "down" | null, note: string | null = null) => {
    if (!isSignedIn) return Promise.resolve();
    return api.askFeedback({ turnId: t.id, rating, question: t.question, answerExcerpt: t.answer.slice(0, EXCERPT_CHARS), comment: note, platform: feedbackPlatform() }).then(
      () => undefined,
      () => undefined,
    );
  };
  const rate = (t: AskTurn, v: "up" | "down") => {
    const next = feedback[t.id] === v ? null : v;
    setFeedback((f) => {
      const copy = { ...f };
      if (next) copy[t.id] = next;
      else delete copy[t.id];
      return copy;
    });
    setCommenting(next === "down" && isSignedIn ? t.id : null);
    setComment("");
    void sendRating(t, next);
  };
  const explain = (t: AskTurn) => {
    const text = comment.trim();
    if (!text) return;
    void sendRating(t, "down", text);
    setExplained((set) => new Set(set).add(t.id));
    setCommenting(null);
    setComment("");
  };

  return (
    <div className={s.askPanel} data-testid="ask-panel">
      <div className="u-row">
        <Bot size={18} />
        <div className="u-grow">
          <div style={{ fontWeight: 600 }}>Ask Hub</div>
          <div className={`${s.small} ${s.muted}`}>Questions about your own people, answered from your data on this device.</div>
        </div>
        {hasKey && (
          <label className="u-row" style={{ gap: 6 }}>
            <span className={s.small}>Use AI</span>
            <Switch on={useAi} onChange={setUseAi} label="Have the model reword answers" />
          </label>
        )}
        {history.length > 0 && (
          <IconButton
            label="Clear history"
            onClick={() => {
              setHistory([]);
            }}
          >
            <Trash2 size={15} />
          </IconButton>
        )}
        {onClose && (
          <IconButton label="Close" onClick={onClose}>
            <X size={16} />
          </IconButton>
        )}
      </div>

      {contactId && (
        <div className="u-row" style={{ gap: 6, flexWrap: "wrap" }} data-testid="ask-context">
          <span className={`${s.small} ${s.muted}`}>Asking about</span>
          <ContactChip contactId={contactId} />
          {contextRow.data?.[0]?.headline && <span className={`${s.small} ${s.muted}`}>{contextRow.data[0].headline}</span>}
        </div>
      )}
      <div className={s.askScroll} ref={scroll}>
        {history.length === 0 && (
          <div className="u-col" style={{ gap: 8 }}>
            <div className={`${s.small} ${s.muted}`}>Try one of these:</div>
            <div className={s.chips} data-testid="ask-starters">
              {(contextRow.data?.[0] ? personStarters(contextRow.data[0]) : starters).map((q) => (
                <button key={q} type="button" className={s.chip} data-selectable="true" onClick={() => submit(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
        {history.map((t) => (
          <div key={t.id} className={s.askTurn} data-testid="ask-turn">
            <div className={s.askQuestion}>{t.question}</div>
            {t.query && (
              <div className="u-row" style={{ flexWrap: "wrap" }}>
                <button type="button" className={s.askQuery} title="Search grammar used; click to copy it" data-testid="ask-query" onClick={() => void navigator.clipboard?.writeText(t.query).then(() => toast("Copied. Paste it into Search or a smart group.", "neutral"))}>
                  {t.query}
                </button>
                {t.chips.map((c) => (
                  <Pill key={c} tone="info">
                    {c}
                  </Pill>
                ))}
                {t.warnings.map((w) => (
                  <Pill key={w} tone="warning">
                    {w}
                  </Pill>
                ))}
                {t.broadened && <Pill tone="neutral">Searched more widely</Pill>}
              </div>
            )}
            <div className={s.askAnswer} data-testid="ask-answer">
              {t.answer}
            </div>
            {t.facts.length > 0 && (
              <ul className={s.askFacts} data-testid="ask-facts">
                {t.facts.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
            {t.people.length > 0 && (
              <div className={s.chips}>
                {t.people.map((p) => (
                  <button key={p.contactId} type="button" className={s.chip} data-selectable="true" onClick={() => open(p.contactId)} title={p.why ?? p.headline ?? p.company ?? undefined}>
                    <Avatar name={p.displayName} src={p.avatarUrl} size={16} seed={p.contactId} />
                    {p.displayName}
                    {p.why && <span className={s.muted}>{p.why}</span>}
                  </button>
                ))}
                {t.total > t.people.length && <span className={`${s.chip} ${s.muted}`}>+{t.total - t.people.length} more</span>}
              </div>
            )}
            <div className={s.askFooter}>
              <span>
                {t.source === "ai" ? (
                  <span className="u-row" style={{ gap: 4 }}>
                    <Sparkles size={12} /> Reworded by the model from these rows
                  </span>
                ) : (
                  "From your data"
                )}
              </span>
              <span className="u-row" style={{ gap: 2 }}>
                <IconButton label="Helpful" active={feedback[t.id] === "up"} onClick={() => rate(t, "up")}>
                  <ThumbsUp size={14} />
                </IconButton>
                <IconButton label="Not helpful" active={feedback[t.id] === "down"} onClick={() => rate(t, "down")}>
                  <ThumbsDown size={14} />
                </IconButton>
              </span>
            </div>
            {commenting === t.id && (
              <form
                className="u-row"
                style={{ gap: 6 }}
                data-testid="ask-feedback-comment"
                onSubmit={(e) => {
                  e.preventDefault();
                  explain(t);
                }}
              >
                <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="What was wrong? (optional)" aria-label="What was wrong" maxLength={2000} autoFocus />
                <Button type="submit" size="sm" disabled={!comment.trim()}>
                  Send
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setCommenting(null)}>
                  Skip
                </Button>
              </form>
            )}
            {explained.has(t.id) && feedback[t.id] === "down" && <div className={`${s.small} ${s.muted}`}>Thanks. That helps make Ask better.</div>}
          </div>
        ))}
        {busy && <div className={`${s.small} ${s.muted}`}>Looking through your people...</div>}
      </div>

      <form
        className={s.askForm}
        onSubmit={(e) => {
          e.preventDefault();
          void submit(question);
        }}
      >
        <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={contextRow.data?.[0] ? `Ask about ${contextRow.data[0].firstName ?? contextRow.data[0].displayName}...` : "Who do I know at..."} aria-label="Ask a question" autoFocus />
        <Button type="submit" variant="primary" loading={busy} disabled={!question.trim()}>
          Ask
        </Button>
      </form>
    </div>
  );
}
